import { EImageVariant } from '@onesource/schemas';
import { getPresignedUploadUrl } from '@onesource/services';
import { readAndCompressImage } from 'browser-image-resizer';
import type { ProcessServerConfigFunction } from 'filepond';
import { enqueueSnackbar } from 'notistack';

/**
 * Pull the human-readable message out of a 403 quota rejection (monthly personal
 * upload limit) returned by the media record-creation endpoint.
 */
export const extractQuotaErrorMessage = (err: unknown): string | null => {
  const response = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
  return response?.status === 403 && response.data?.message ? response.data.message : null;
};

import { compressPdfFile } from '../media/compress-pdf';

export type TUploadMeta = {
  key: string;
  cdnUrls: Record<EImageVariant, string>;
  cdnSizes: Record<EImageVariant, number>;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
};

type TVariantConfig = {
  variant: EImageVariant;
  maxWidth: number | undefined;
  maxHeight: number | undefined;
  suffix: string;
};

export const VARIANT_CONFIGS: TVariantConfig[] = [
  { variant: EImageVariant.w90, maxWidth: 90, maxHeight: 90, suffix: '-90x90' },
  { variant: EImageVariant.w230x280, maxWidth: 230, maxHeight: 280, suffix: '-230x280' },
  { variant: EImageVariant.w400, maxWidth: 400, maxHeight: 400, suffix: '-400x400' },
  { variant: EImageVariant.w715x500, maxWidth: 715, maxHeight: 500, suffix: '-715x500' },
  { variant: EImageVariant.w800, maxWidth: 800, maxHeight: 800, suffix: '-800x800' },
  { variant: EImageVariant.w1070, maxWidth: 1070, maxHeight: 600, suffix: '-1070x600' },
  { variant: EImageVariant.w1200, maxWidth: 1200, maxHeight: 1200, suffix: '-1200x1200' },
  { variant: EImageVariant.w1375, maxWidth: 1375, maxHeight: 950, suffix: '-1375x950' },
  { variant: EImageVariant.w1920x768, maxWidth: 1920, maxHeight: 768, suffix: '-1920x768' },
  { variant: EImageVariant.w1986x558, maxWidth: 1986, maxHeight: 558, suffix: '-1986x558' },
  { variant: EImageVariant.w2400, maxWidth: 2400, maxHeight: 2400, suffix: '-2400x2400' },
  { variant: EImageVariant.original, maxWidth: undefined, maxHeight: undefined, suffix: '' },
];

const uploadToS3 = (
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) {
        onProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error('Upload failed'));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });

const resizeImage = async (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
  return readAndCompressImage(file, {
    quality: 0.92,
    maxWidth,
    maxHeight,
    mimeType: 'image/webp',
  });
};

/**
 * Public path of an uploaded object, preserving the raw R2 key exactly.
 *
 * Deliberately NOT `new URL(publicUrl).pathname`: pathname percent-encodes the
 * path, and this value is stored in `cdnUrls` and later handed back to R2 as an
 * object `Key` (transcode dispatch, retention sweep). The SDK encodes keys
 * itself, so a pre-encoded key double-encodes (`my file.pdf` → `my%2520file.pdf`)
 * and 404s — which strands compressible uploads (video/PDF) in PROCESSING
 * forever. slice() off the origin keeps spaces (and `#`) intact.
 */
const toStoredPath = (publicUrl: string): string =>
  publicUrl.startsWith('http') ? publicUrl.slice(new URL(publicUrl).origin.length) : publicUrl;

export const buildProcessHandler =
  (enabledVariants: EImageVariant[] = Object.values(EImageVariant)): ProcessServerConfigFunction =>
  async (_fieldName, file, _metadata, load, error, progress, abort) => {
    const f = file as File;
    const isImage = f.type.startsWith('image/');
    const baseName = `${f.name.replace(/\.[^.]+$/, '')}-${Date.now()}`;
    const extension = f.name.includes('.') ? f.name.split('.').pop()! : ''; // used for key/url construction only

    let aborted = false;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const abortController = { abort: () => {} };

    try {
      let cdnUrls: Record<EImageVariant, string>;

      let cdnSizes: Record<EImageVariant, number>;

      // Bytes actually uploaded to R2 — drives both the quota check and the saved record.
      // For images this is the sum of the resized variant blobs, for PDFs the compressed
      // blob — NOT the (much larger) source file.
      let uploadedBytes = f.size;

      if (isImage) {
        const activeVariants = VARIANT_CONFIGS.filter(v => enabledVariants.includes(v.variant));
        const urlEntries: Array<[EImageVariant, string]> = [];
        const sizeEntries: Array<[EImageVariant, number]> = [];
        const variantCount = activeVariants.length;

        for (let i = 0; i < activeVariants.length; i++) {
          if (aborted) {
            return;
          }
          const { variant, maxWidth, maxHeight, suffix } = activeVariants[i];

          const variantBlob =
            maxWidth && maxHeight ? await resizeImage(f, maxWidth, maxHeight) : new Blob([f], { type: f.type });

          const variantContentType = maxWidth ? 'image/webp' : f.type;
          const variantExt = maxWidth ? 'webp' : extension;
          const variantKey = `${baseName}${suffix}.${variantExt}`;

          const { uploadUrl, publicUrl } = await getPresignedUploadUrl(variantKey, variantContentType);

          if (!publicUrl) {
            error('No public URL returned for variant');
            return;
          }

          await uploadToS3(uploadUrl, variantBlob, variantContentType, (loaded, total) => {
            // Weight progress across all variants
            const variantProgress = loaded / total;
            const overallProgress = (i + variantProgress) / variantCount;
            progress(true, Math.round(overallProgress * f.size), f.size);
          });

          urlEntries.push([variant, toStoredPath(publicUrl)]);
          sizeEntries.push([variant, variantBlob.size]);
        }

        cdnUrls = Object.fromEntries(urlEntries) as Record<EImageVariant, string>;
        cdnSizes = Object.fromEntries(sizeEntries) as Record<EImageVariant, number>;
        uploadedBytes = sizeEntries.reduce((sum, [, size]) => sum + size, 0);
      } else {
        // Non-image (PDF) — compress in the browser, then upload the single
        // final object. PDFs no longer go through the server-side transcoder.
        const uploadBlob = await compressPdfFile(f);
        uploadedBytes = uploadBlob.size;

        const key = `${baseName}.${extension}`;
        const { uploadUrl, publicUrl } = await getPresignedUploadUrl(key, f.type);

        if (!publicUrl) {
          error('No public URL returned');
          return;
        }

        await uploadToS3(uploadUrl, uploadBlob, f.type, (loaded, total) => progress(true, loaded, total));

        cdnUrls = { [EImageVariant.original]: toStoredPath(publicUrl) } as Record<EImageVariant, string>;
        cdnSizes = { [EImageVariant.original]: uploadedBytes } as Record<EImageVariant, number>;
      }

      const meta: TUploadMeta = {
        key: baseName,
        cdnUrls,
        cdnSizes,
        originalName: f.name,
        mimeType: f.type,
        sizeBytes: uploadedBytes,
      };

      load(JSON.stringify(meta));
      // oxlint-disable-next-line unicorn/catch-error-name -- naming it `error` shadows FilePond's error callback
    } catch (err) {
      if (!aborted) {
        // If the BE rejected the request with a structured quota error, surface the human
        // message it built; otherwise fall back to the generic failure string.
        // NOTE: the catch variable must not be named `error` — it would shadow FilePond's
        // `error` callback and the message would never reach the UI.
        const quotaMessage = extractQuotaErrorMessage(err);
        error(quotaMessage ?? 'Upload failed');
        // FilePond renders the error inside the (small) file item — also raise a snackbar
        // so quota rejections (e.g. storage / monthly 5MB limit) are impossible to miss.
        if (quotaMessage) {
          enqueueSnackbar(quotaMessage, { variant: 'error' });
        }
      }
    }

    return {
      abort: () => {
        aborted = true;
        abortController.abort();
        abort();
      },
    };
  };
